import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { GameState, Item, EquipSlot, ItemSlot } from '../store/types'
import { apiInvEquip, apiInvUnequip, apiInvDrop, apiItemEnhance, apiUseItem } from '../server/ServiceCall'

const SLOT_LABELS: Record<string, string> = {
  WEAPON:'무기', CLOTH:'도복', HAT:'모자', GLOVE:'장갑',
  SHOE:'신발', CLOAK:'망토', INNER:'속옷', NECK:'목걸이',
  BRACELET_1:'팔찌①', BRACELET_2:'팔찌②',
}
const GRADE_COLOR: Record<string, string> = {
  COMMON:'#b0b0b0', UNCOMMON:'#60c060', RARE:'#4090e0',
  EPIC:'#a060e0',   LEGEND:'#e0a030',   MYTH:'#e04060',
}

const LEFT_SLOTS:  EquipSlot[] = ['HAT', 'WEAPON', 'CLOTH', 'GLOVE', 'SHOE']
const RIGHT_SLOTS: EquipSlot[] = ['CLOAK', 'INNER', 'NECK', 'BRACELET_1', 'BRACELET_2']

const BOX_BG = { backgroundImage: "url('/images/invan_box.png')" } as const

// 이미지 없는 아이템 ID 캐시 - 컴포넌트 재마운트 시 깜빡임 방지
const noImgSet = new Set<string>()

function ItemIcon({ itemId, name, grade, enhanceLevel, qty }: {
  itemId: string; name: string; grade: string; enhanceLevel?: number; qty?: number
}) {
  const [hasImg, setHasImg] = useState(!noImgSet.has(itemId))
  return (
    <div className="inven-box-inner" style={{ color: GRADE_COLOR[grade] }}>
      {hasImg ? (
        <img
          src={`/images/items/${itemId}.svg`}
          alt={name}
          className="inven-item-img"
          onError={() => { noImgSet.add(itemId); setHasImg(false) }}
        />
      ) : (
        <div className="inven-box-name">{name.length > 4 ? name.slice(0, 4) : name}</div>
      )}
      {enhanceLevel != null && enhanceLevel > 0 && (
        <div style={{ fontSize:9, color:'#e0c060', fontWeight:'bold', lineHeight:1 }}>+{enhanceLevel}</div>
      )}
      {qty != null && qty > 1 && <div className="inven-box-qty">×{qty}</div>}
    </div>
  )
}

interface Props {
  gs:        GameState
  token:     string
  onRefresh: () => void
}

const CATEGORY_KO: Record<string, string> = {
  EQUIP:'장비', CONSUME:'소모품', MUGONG_BOOK:'비급', MATERIAL:'재료', HERB:'약초',
}

export default function TabInven({ gs, token, onRefresh }: Props) {
  const [subTab,      setSubTab]     = useState<'equip' | 'bag'>('equip')
  const [selected,    setSelected]   = useState<Item | null>(null)
  const [busy,        setBusy]       = useState(false)
  const [msg,         setMsg]        = useState('')
  const [msgOk,       setMsgOk]      = useState(false)
  const [dropQty,     setDropQty]    = useState(1)
  const [slotPick,    setSlotPick]   = useState<'BRACELET_1'|'BRACELET_2'>('BRACELET_1')
  const [tooltip,     setTooltip]    = useState<{ item: Item; x: number; y: number } | null>(null)
  const [ctxMenu,     setCtxMenu]    = useState<{ item: Item; x: number; y: number } | null>(null)
  const [ctxDropQty,  setCtxDropQty] = useState(1)
  const [scrollInvId, setScrollInvId] = useState<number | null>(null)
  const [enhMsg,      setEnhMsg]     = useState('')
  const [enhOk,       setEnhOk]      = useState(false)
  const [enhImpact,   setEnhImpact]  = useState<'success' | 'maintain' | 'destroy' | null>(null)
  const [bagCatTab,   setBagCatTab]  = useState<string>('전체')
  const ctxRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!ctxMenu) return
    function handleOutside(e: MouseEvent) {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) {
        setCtxMenu(null)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [ctxMenu])

  // 인벤토리 갱신 시 선택 아이템 최신 데이터로 동기화 (강화 후 패널 유지)
  useEffect(() => {
    if (!selected) return
    const fresh = gs.inventory.find(i => i.invId === selected.invId)
    if (fresh) setSelected(fresh)
    else setSelected(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gs.inventory])

  const equipped = new Map<string, Item>()
  const bag: Item[] = []
  for (const item of gs.inventory) {
    if (item.equipSlot) equipped.set(item.equipSlot, item)
    else                bag.push(item)
  }

  function showMsg(m: string, ok = false) {
    setMsg(m); setMsgOk(ok); setTimeout(() => setMsg(''), 2500)
  }
  function selectItem(item: Item | null) { setSelected(item); setDropQty(1); setEnhMsg('') }

  const scrollItems = gs.inventory.filter(it =>
    !it.equipSlot && (it.itemId === 'SCROLL_ENHANCE' || it.itemId === 'SCROLL_BLESSED')
  )

  function calcEnhanceRates(level: number, scrollId: string | null) {
    let success = Math.max(5, 90 - level * 10)
    let destroy = Math.min(80, (level + 1) * 15)
    if (scrollId === 'SCROLL_BLESSED') {
      success = Math.min(95, success + 10)
      destroy = Math.max(5, Math.floor(destroy / 2))
    }
    return { success, destroy }
  }

  async function doEnhance() {
    if (!selected || scrollInvId === null) return
    setBusy(true)
    try {
      const res = await apiItemEnhance(token, selected.invId, scrollInvId)
      const destroyed = res.resultCode === '02'
      const success   = res.resultCode === '00'
      setEnhOk(success)
      setEnhMsg(res.resultMessage ?? '')
      const impact = destroyed ? 'destroy' : success ? 'success' : 'maintain'
      setEnhImpact(impact)
      setTimeout(() => setEnhImpact(null), 1800)
      onRefresh()
    } catch { setEnhMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doEquip(item: Item, slot: EquipSlot) {
    setBusy(true)
    try {
      const res = await apiInvEquip(token, item.invId, slot)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { selectItem(null); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }
  async function doUnequip(item: Item) {
    setBusy(true)
    try {
      const res = await apiInvUnequip(token, item.invId)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { selectItem(null); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }
  async function doUse(item: Item) {
    setBusy(true)
    try {
      const res = await apiUseItem(token, item.invId)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { selectItem(null); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doDrop(item: Item) {
    if (!window.confirm(`[${item.name}] × ${dropQty}개를 버리겠습니까?`)) return
    setBusy(true)
    try {
      const res = await apiInvDrop(token, item.invId, dropQty)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { selectItem(null); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  function openCtxMenu(item: Item, e: React.MouseEvent) {
    e.preventDefault()
    setTooltip(null)
    setCtxDropQty(1)
    setCtxMenu({ item, x: e.clientX, y: e.clientY })
  }

  /* ── 컨텍스트 메뉴 ── */
  function BagContextMenu() {
    if (!ctxMenu) return null
    const { item, x, y } = ctxMenu
    const menuW = 160
    const menuH = 200
    const left = x + menuW > window.innerWidth  ? x - menuW : x
    const top  = y + menuH > window.innerHeight ? y - menuH : y

    async function handleEquip() {
      setCtxMenu(null)
      if ((item.slot as ItemSlot) === 'BRACELET') {
        // 팔찌는 slotPick 기본값으로 장착
        await doEquip(item, slotPick)
      } else {
        await doEquip(item, item.slot as EquipSlot)
      }
    }
    async function handleUse()  { setCtxMenu(null); await doUse(item) }
    async function handleDrop() {
      setCtxMenu(null)
      setDropQty(ctxDropQty)
      await doDrop({ ...item })
    }

    return createPortal(
      <div
        ref={ctxRef}
        className="bag-ctx-menu"
        style={{ left, top }}
        onContextMenu={e => e.preventDefault()}
      >
        <div className="bag-ctx-title" style={{ color: GRADE_COLOR[item.grade] }}>
          {item.name}{item.enhanceLevel > 0 && <span style={{ color:'#e0c060', marginLeft:4 }}>+{item.enhanceLevel}</span>}
        </div>
        {item.category === 'EQUIP' && (
          (item.slot as ItemSlot) === 'BRACELET' ? (
            <div className="bag-ctx-bracelet-row">
              <select className="slot-select" value={slotPick}
                onChange={e => setSlotPick(e.target.value as any)}>
                <option value="BRACELET_1">팔찌①</option>
                <option value="BRACELET_2">팔찌②</option>
              </select>
              <button className="bag-ctx-btn equip" disabled={busy} onClick={handleEquip}>장착</button>
            </div>
          ) : (
            <button className="bag-ctx-btn equip" disabled={busy} onClick={handleEquip}>장착</button>
          )
        )}
        {(item.category === 'CONSUME' || item.category === 'HERB') && (
          <button className="bag-ctx-btn use" disabled={busy} onClick={handleUse}>
            {item.consumeType === 'PERM' ? '⭐ 복용 (영구)' : '⚕ 복용 (회복)'}
          </button>
        )}
        {item.qty > 1 && !['EQUIP','MUGONG_BOOK'].includes(item.category) && (
          <div className="bag-ctx-drop-row">
            <input type="number" className="drop-qty-input"
              min={1} max={item.qty} value={ctxDropQty}
              onChange={e => setCtxDropQty(Math.max(1, Math.min(item.qty, Number(e.target.value))))} />
            <button className="bag-ctx-btn drop" disabled={busy} onClick={handleDrop}>버리기</button>
          </div>
        )}
        {(item.qty === 1 || ['EQUIP','MUGONG_BOOK'].includes(item.category)) && (
          <button className="bag-ctx-btn drop" disabled={busy} onClick={handleDrop}>버리기</button>
        )}
      </div>,
      document.body
    )
  }

  /* ── 툴팁 ── */
  function ItemTooltip() {
    if (!tooltip) return null
    const { item, x, y } = tooltip
    const hasStats = item.atkBonus > 0 || item.defBonus > 0 || item.hpBonus > 0 || item.speedBonus > 0
    // 앱 컨테이너(max 480px, 중앙 정렬) 내에 툴팁 위치 제한
    const appEl = document.querySelector('.app-container')
    const appRect = appEl ? appEl.getBoundingClientRect() : { left: 0, right: window.innerWidth }
    const tipW = 165
    const rawLeft = x + tipW > appRect.right ? x - tipW : x + 12
    const left = Math.max(appRect.left, Math.min(rawLeft, appRect.right - tipW))
    const top  = Math.min(y - 10, window.innerHeight - 200)
    // MUGONG_BOOK: 습득한 무공 스탯 조회
    const linkedMugong = item.category === 'MUGONG_BOOK' && item.mugongId
      ? gs.mugongs.find(m => m.mugongId === item.mugongId) ?? null
      : null
    const GRADE_KO: Record<string, string> = {
      COMMON:'일반', UNCOMMON:'고급', RARE:'희귀', EPIC:'영웅', LEGEND:'전설', MYTH:'신화',
    }
    return createPortal(
      <div className="inven-tooltip" style={{ left, top }}>
        <div className="inven-tooltip-name" style={{ color: GRADE_COLOR[item.grade] }}>
          {item.name}{item.enhanceLevel > 0 ? <span style={{ color:'#e0c060', marginLeft:4 }}>+{item.enhanceLevel}</span> : null}
        </div>
        <div className="inven-tooltip-grade">{GRADE_KO[item.grade] ?? item.grade}</div>
        <div className="inven-tooltip-cat">
          {CATEGORY_KO[item.category] ?? item.category}
          {item.slot && item.category === 'EQUIP' && ` · ${SLOT_LABELS[item.slot] ?? item.slot}`}
          {item.equipSlot && <span className="inven-tooltip-equip-badge">장착 중</span>}
        </div>
        {/* 소모품: 효과 타입별 표시 */}
        {(item.category === 'CONSUME' || item.category === 'HERB') && item.consumeType === 'HEAL' && item.hpBonus > 0 && (
          <div className="inven-tooltip-stats">
            <span style={{ color:'#60d060' }}>⚕ 체력 회복 +{item.hpBonus >= 99999 ? '완전회복' : item.hpBonus}</span>
          </div>
        )}
        {(item.category === 'CONSUME' || item.category === 'HERB') && item.consumeType === 'PERM' && (
          <div className="inven-tooltip-stats">
            <div style={{ color:'#e0c060', marginBottom:2, fontSize:10 }}>⭐ 영구 스탯 증가</div>
            {item.atkBonus   > 0 && <span style={{ color:'#ff8080' }}>공격 +{item.atkBonus}</span>}
            {item.defBonus   > 0 && <span style={{ color:'#80c0ff' }}>방어 +{item.defBonus}</span>}
            {item.hpBonus    > 0 && <span style={{ color:'#80ff80' }}>최대체력 +{item.hpBonus}</span>}
            {item.speedBonus > 0 && <span style={{ color:'#ffcc80' }}>속도 +{item.speedBonus}</span>}
          </div>
        )}
        {/* 장비: 스탯 표시 */}
        {item.category === 'EQUIP' && hasStats && (
          <div className="inven-tooltip-stats">
            {item.atkBonus     > 0 && <span>공격 +{item.atkBonus}</span>}
            {item.defBonus     > 0 && <span>방어 +{item.defBonus}</span>}
            {item.hpBonus    > 0 && <span>체력 +{item.hpBonus}</span>}
            {item.speedBonus > 0 && <span>속도 +{item.speedBonus}</span>}
          </div>
        )}
        {item.category === 'MUGONG_BOOK' && (
          linkedMugong ? (
            <div className="inven-tooltip-stats">
              <div style={{ color:'#c8b890', marginBottom:2 }}>습득 무공 능력치 ({linkedMugong.star}성)</div>
              {linkedMugong.atkBonus > 0 && <span>공격 +{linkedMugong.atkBonus * linkedMugong.star}</span>}
              {linkedMugong.defBonus > 0 && <span>방어 +{linkedMugong.defBonus * linkedMugong.star}</span>}
              {linkedMugong.hpBonus  > 0 && <span>체력 +{linkedMugong.hpBonus  * linkedMugong.star}</span>}
            </div>
          ) : (
            <div className="inven-tooltip-note">습득 시 무공 획득</div>
          )
        )}
        {item.qty > 1 && <div className="inven-tooltip-qty">수량 {item.qty}개</div>}
      </div>,
      document.body
    )
  }

  /* ── 슬롯 박스 컴포넌트 ── */
  function SlotBox({ slot }: { slot: EquipSlot }) {
    const item  = equipped.get(slot)
    const isSel = selected?.equipSlot === slot
    return (
      <div
        className={`inven-slot-box${isSel ? ' selected' : ''}`}
        style={BOX_BG}
        title={SLOT_LABELS[slot]}
        onClick={() => item && selectItem(isSel ? null : item)}
        onMouseEnter={e => item && setTooltip({ item, x: e.clientX, y: e.clientY })}
        onMouseMove={e  => item && setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
        onMouseLeave={() => setTooltip(null)}
      >
        {item ? (
          <ItemIcon itemId={item.itemId} name={item.name} grade={item.grade} enhanceLevel={item.enhanceLevel} />
        ) : (
          <div className="inven-box-label">{SLOT_LABELS[slot]}</div>
        )}
      </div>
    )
  }

  /* ── 액션 바 ── */
  const actionBar = selected && (
    <div className="inven-action-bar">
      <div className="inven-action-left">
        <span className="inven-action-name" style={{ color: GRADE_COLOR[selected.grade] }}>
          {selected.name}{selected.enhanceLevel > 0 && <span style={{ color:'#e0c060', marginLeft:4 }}>+{selected.enhanceLevel}</span>}
        </span>
        <span className="inven-action-stats">
          {selected.atkBonus     > 0 && ` 공+${selected.atkBonus}`}
          {selected.defBonus     > 0 && ` 방+${selected.defBonus}`}
          {selected.hpBonus    > 0 && ` 체+${selected.hpBonus}`}
          {selected.speedBonus > 0 && ` 속+${selected.speedBonus}`}
        </span>
      </div>
      <div className="inven-action-btns">
        {selected.equipSlot && (
          <button className="action-btn unequip" disabled={busy} onClick={() => doUnequip(selected)}>해제</button>
        )}
        {!selected.equipSlot && (selected.category === 'CONSUME' || selected.category === 'HERB') && (
          <button className="action-btn equip" disabled={busy} onClick={() => doUse(selected)}>사용</button>
        )}
        {!selected.equipSlot && selected.category === 'EQUIP' && (
          <>
            {(selected.slot as ItemSlot) === 'BRACELET' ? (
              <>
                <select className="slot-select" value={slotPick}
                  onChange={e => setSlotPick(e.target.value as any)}>
                  <option value="BRACELET_1">팔찌①</option>
                  <option value="BRACELET_2">팔찌②</option>
                </select>
                <button className="action-btn equip" disabled={busy}
                  onClick={() => doEquip(selected, slotPick)}>장착</button>
              </>
            ) : (
              <button className="action-btn equip" disabled={busy}
                onClick={() => doEquip(selected, selected.slot as EquipSlot)}>장착</button>
            )}
          </>
        )}
        {!selected.equipSlot && (
          <>
            {selected.qty > 1 && !['EQUIP','MUGONG_BOOK'].includes(selected.category) && (
              <input type="number" className="drop-qty-input"
                min={1} max={selected.qty} value={dropQty}
                onChange={e => setDropQty(Math.max(1, Math.min(selected.qty, Number(e.target.value))))} />
            )}
            <button className="action-btn drop" disabled={busy} onClick={() => doDrop(selected)}>버리기</button>
          </>
        )}
        <button className="action-btn" style={{ background:'#2a1a08', marginLeft:4 }}
          onClick={() => selectItem(null)}>✕</button>
      </div>
    </div>
  )

  return (
    <div className="tab-inven-new">

      {/* 서브탭 */}
      <div className="inven-subtab-nav">
        <button
          className={`inven-subtab-btn${subTab === 'equip' ? ' active' : ''}`}
          onClick={() => { setSubTab('equip'); selectItem(null) }}
        >장착</button>
        <button
          className={`inven-subtab-btn${subTab === 'bag' ? ' active' : ''}`}
          onClick={() => { setSubTab('bag'); selectItem(null) }}
        >
          행낭
          {bag.length > 0 && <span className="inven-bag-count">{bag.length}</span>}
        </button>
      </div>

      {/* ── 장착 탭 ── */}
      {subTab === 'equip' && (
        <div className="inven-equip-tab" onMouseLeave={() => setTooltip(null)}>
          <div className="inven-equip-layout">
            {/* 왼쪽 슬롯 열 */}
            <div className="inven-slot-col">
              {LEFT_SLOTS.map(slot => <SlotBox key={slot} slot={slot} />)}
            </div>

            {/* 캐릭터 이미지 */}
            <div className="inven-char-center">
              <img src="/images/invan_cha.png" alt="캐릭터" className="inven-char-img" />
            </div>

            {/* 오른쪽 슬롯 열 */}
            <div className="inven-slot-col">
              {RIGHT_SLOTS.map(slot => <SlotBox key={slot} slot={slot} />)}
            </div>
          </div>
          {actionBar}
        </div>
      )}

      {/* ── 행낭 탭 ── */}
      {subTab === 'bag' && (
        <div className="inven-bag-tab">
          {/* 카테고리 필터 탭 */}
          {(() => {
            const presentCats = ['전체', ...Object.keys(CATEGORY_KO).filter(c => bag.some(i => i.category === c))]
            return presentCats.length > 1 && (
              <div className="bag-cat-tabs">
                {presentCats.map(c => (
                  <button
                    key={c}
                    className={`bag-cat-btn${bagCatTab === c ? ' active' : ''}`}
                    onClick={() => { setBagCatTab(c); selectItem(null) }}
                  >
                    {c === '전체' ? '전체' : CATEGORY_KO[c]}
                  </button>
                ))}
              </div>
            )
          })()}
          <div className="inven-bag-grid">
            {(() => {
              const filtered = bagCatTab === '전체' ? bag : bag.filter(i => i.category === bagCatTab)
              return Array.from({ length: Math.max(36, Math.ceil(filtered.length / 6) * 6) }, (_, i) => {
              const item = filtered[i] ?? null
              const isSel = item !== null && selected?.invId === item.invId
              return (
                <div
                  key={i}
                  className={`inven-slot-box${item ? ' has-item' : ''}${isSel ? ' selected' : ''}`}
                  style={BOX_BG}
                  onClick={e => {
                    if (!item) return
                    if (item.category === 'EQUIP') {
                      selectItem(isSel ? null : item)
                    } else {
                      openCtxMenu(item, e)
                    }
                  }}
                  onContextMenu={e => { e.preventDefault(); item && openCtxMenu(item, e) }}
                  onMouseEnter={e => item && !ctxMenu && setTooltip({ item, x: e.clientX, y: e.clientY })}
                  onMouseMove={e  => item && !ctxMenu && setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null)}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {item && (
                    <ItemIcon itemId={item.itemId} name={item.name} grade={item.grade} enhanceLevel={item.enhanceLevel} qty={item.qty} />
                  )}
                </div>
              )
            })
            })()}
          </div>
          {actionBar}
        </div>
      )}

      {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`} style={{ marginTop:4 }}>{msg}</div>}

      {/* ── 강화 패널 ── */}
      {selected && selected.category === 'EQUIP' && (() => {
        const pickedScroll = scrollItems.find(s => s.invId === scrollInvId) ?? null
        const { success, destroy } = calcEnhanceRates(selected.enhanceLevel, pickedScroll?.itemId ?? null)
        return (
          <div style={{
            marginTop: 8, background:'#120c04', border:'1px solid #6a3a10',
            borderRadius: 4, padding:'10px 12px',
          }}>
            {enhImpact && (
              <div className={`enh-impact enh-impact-${enhImpact}`}>
                <div className="enh-impact-text">
                  {enhImpact === 'success'  && '⚡ 강화 성공!'}
                  {enhImpact === 'maintain' && '💨 강화 실패'}
                  {enhImpact === 'destroy'  && '💥 장비 소멸!'}
                </div>
                {enhImpact === 'success' && selected && (
                  <div className="enh-impact-level">+{selected.enhanceLevel}</div>
                )}
              </div>
            )}
            <div style={{ fontSize:12, color:'#c8820a', fontWeight:'bold', marginBottom:6 }}>⚒ 장비 강화</div>
            <div style={{ display:'flex', gap:16, fontSize:11, color:'#a09060', marginBottom:8, flexWrap:'wrap' }}>
              <span>현재 단계 <strong style={{ color:'#e0c060' }}>+{selected.enhanceLevel}</strong></span>
              <span>성공 <strong style={{ color:'#60c060' }}>{success}%</strong></span>
              <span>실패 시 소멸 <strong style={{ color:'#e06060' }}>{destroy}%</strong></span>
            </div>
            {scrollItems.length === 0 ? (
              <div style={{ fontSize:11, color:'#604030' }}>강화 주문서가 없습니다. (사냥터 드롭 전용)</div>
            ) : (
              <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                <select
                  className="slot-select"
                  value={scrollInvId ?? ''}
                  onChange={e => setScrollInvId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">주문서 선택</option>
                  {scrollItems.map(s => (
                    <option key={s.invId} value={s.invId}>
                      {s.name} ×{s.qty}
                    </option>
                  ))}
                </select>
                <button
                  className="action-btn"
                  style={{
                    background:'#2a1808', borderColor:'#8a5020', color:'#f0c060',
                    padding:'5px 16px', fontSize:12,
                  }}
                  disabled={busy || scrollInvId === null}
                  onClick={doEnhance}
                >
                  강화 시도
                </button>
              </div>
            )}
            {enhMsg && (
              <div style={{
                marginTop:6, fontSize:12, padding:'4px 8px', borderRadius:3,
                background: enhOk ? '#0a2010' : '#2a0808',
                border: `1px solid ${enhOk ? '#2a6020' : '#803030'}`,
                color: enhOk ? '#70d070' : '#f07070',
              }}>{enhMsg}</div>
            )}
          </div>
        )
      })()}

      <ItemTooltip />
      <BagContextMenu />
    </div>
  )
}
